import { Suspense } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
// `lazy` = stale-chunk auto-reload sarmalayıcı (founder 2026-06-23): yeni deploy
// eski hash'li chunk'ları silince açık eski sekme gezinince patlamasın diye route
// import'u başarısız olursa sayfayı bir kez otomatik yeniler. Bkz. lazyWithReload.
import { lazyWithReload as lazy } from './lib/lazyWithReload';
import { AdminOnly } from './components/AdminOnly';
import { RequireAbility } from './components/RequireAbility';
import { RequireRole } from './components/RequireRole';
import { RequireActiveCompany } from './components/auth/RequireActiveCompany';
import { SELF_ONLY_ROLES } from './lib/roles';
import { LoginPage } from './pages/auth/LoginPage';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage';
import { SignupPage } from './pages/auth/SignupPage';
import { VerifyEmailPage } from './pages/auth/VerifyEmailPage';
import { MfaChallengePage } from './pages/auth/MfaChallengePage';
import { EmailOtpPage } from './pages/auth/EmailOtpPage';
import { PhoneVerifyPage } from './pages/auth/PhoneVerifyPage';
import { AcceptInvitePage } from './pages/auth/AcceptInvitePage';
import { RequireAuth } from './components/RequireAuth';
import { RequirePlatform } from './components/RequirePlatform';
import { DashboardLayout } from './components/DashboardLayout';
import { PlatformLayout } from './components/PlatformLayout';
import { CardSkeleton } from './components/Skeleton';
import { NotFoundPage } from './pages/NotFoundPage';

/* Sprint 12 — Code splitting:
 * Dashboard sayfaları React.lazy ile per-route bundle'a ayrıldı; ilk yükleme
 * sadece auth + layout + Overview için gerekli JS'i indirir. Diğer sayfalar
 * navigation anında fetch edilir. Vite production build'inde her route ayrı
 * chunk olur (build size raporu için `pnpm build`). */

const OverviewPage = lazy(() => import('./pages/dashboard/OverviewPage').then((m) => ({ default: m.OverviewPage })));
const EmployeesPage = lazy(() => import('./pages/dashboard/EmployeesPage').then((m) => ({ default: m.EmployeesPage })));
const EmployeeDetailPage = lazy(() =>
  import('./pages/dashboard/EmployeeDetailPage').then((m) => ({ default: m.EmployeeDetailPage })),
);
const CompanyPage = lazy(() => import('./pages/dashboard/CompanyPage').then((m) => ({ default: m.CompanyPage })));
const CompanyRegistrationPage = lazy(() =>
  import('./pages/dashboard/CompanyRegistrationPage').then((m) => ({ default: m.CompanyRegistrationPage })),
);
const DepartmentsPage = lazy(() =>
  import('./pages/dashboard/DepartmentsPage').then((m) => ({ default: m.DepartmentsPage })),
);
const CardsPage = lazy(() => import('./pages/dashboard/CardsPage').then((m) => ({ default: m.CardsPage })));
const WalletPage = lazy(() => import('./pages/dashboard/WalletPage').then((m) => ({ default: m.WalletPage })));
const TransactionsPage = lazy(() =>
  import('./pages/dashboard/TransactionsPage').then((m) => ({ default: m.TransactionsPage })),
);
const AlarmsPage = lazy(() => import('./pages/dashboard/AlarmsPage').then((m) => ({ default: m.AlarmsPage })));
const ReceiptsPage = lazy(() => import('./pages/dashboard/ReceiptsPage').then((m) => ({ default: m.ReceiptsPage })));
const EInvoicesPage = lazy(() => import('./pages/dashboard/EInvoicesPage').then((m) => ({ default: m.EInvoicesPage })));
const AccountingPage = lazy(() =>
  import('./pages/dashboard/AccountingPage').then((m) => ({ default: m.AccountingPage })),
);
const ReportsPage = lazy(() => import('./pages/dashboard/ReportsPage').then((m) => ({ default: m.ReportsPage })));
const BudgetsPage = lazy(() => import('./pages/dashboard/BudgetsPage').then((m) => ({ default: m.BudgetsPage })));
const ProfilePage = lazy(() => import('./pages/dashboard/ProfilePage').then((m) => ({ default: m.ProfilePage })));
const NotificationPreferencesPage = lazy(() =>
  import('./pages/dashboard/NotificationPreferencesPage').then((m) => ({ default: m.NotificationPreferencesPage })),
);
const OrgChartPage = lazy(() => import('./pages/dashboard/OrgChartPage').then((m) => ({ default: m.OrgChartPage })));
const RoleMatrixPage = lazy(() =>
  import('./pages/dashboard/RoleMatrixPage').then((m) => ({ default: m.RoleMatrixPage })),
);
const BillingPage = lazy(() => import('./pages/dashboard/BillingPage').then((m) => ({ default: m.BillingPage })));
const ReimbursementsPage = lazy(() =>
  import('./pages/dashboard/ReimbursementsPage').then((m) => ({ default: m.ReimbursementsPage })),
);
const AdminEnterpriseIdentityPage = lazy(() =>
  import('./pages/dashboard/AdminEnterpriseIdentityPage').then((m) => ({ default: m.AdminEnterpriseIdentityPage })),
);
const EmergencyPage = lazy(() => import('./pages/dashboard/EmergencyPage').then((m) => ({ default: m.EmergencyPage })));
const ApprovalsPage = lazy(() => import('./pages/dashboard/ApprovalsPage').then((m) => ({ default: m.ApprovalsPage })));
const ApprovalPoliciesPage = lazy(() =>
  import('./pages/dashboard/ApprovalPoliciesPage').then((m) => ({ default: m.ApprovalPoliciesPage })),
);
const PricingPage = lazy(() => import('./pages/PricingPage').then((m) => ({ default: m.PricingPage })));
const OnboardingWizard = lazy(() =>
  import('./pages/onboarding/OnboardingWizard').then((m) => ({ default: m.OnboardingWizard })),
);

/* Platform (Custel operatör) konsolu — çapraz-tenant, ayrı layout + guard. */
const PlatformDashboardPage = lazy(() =>
  import('./pages/platform/PlatformDashboardPage').then((m) => ({ default: m.PlatformDashboardPage })),
);
const CustomersListPage = lazy(() =>
  import('./pages/platform/CustomersListPage').then((m) => ({ default: m.CustomersListPage })),
);
const CustomerDetailPage = lazy(() =>
  import('./pages/platform/CustomerDetailPage').then((m) => ({ default: m.CustomerDetailPage })),
);
const CompanyCreatePage = lazy(() =>
  import('./pages/platform/CompanyCreatePage').then((m) => ({ default: m.CompanyCreatePage })),
);
const SystemMetricsPage = lazy(() =>
  import('./pages/platform/SystemMetricsPage').then((m) => ({ default: m.SystemMetricsPage })),
);
const SupportTicketsPage = lazy(() =>
  import('./pages/platform/SupportTicketsPage').then((m) => ({ default: m.SupportTicketsPage })),
);
const PlatformAccessLogsPage = lazy(() =>
  import('./pages/platform/PlatformAccessLogsPage').then((m) => ({ default: m.PlatformAccessLogsPage })),
);
const PlatformStaffPage = lazy(() =>
  import('./pages/platform/PlatformStaffPage').then((m) => ({ default: m.PlatformStaffPage })),
);
const PlatformRuntimeSettingsPage = lazy(() =>
  import('./pages/platform/PlatformRuntimeSettingsPage').then((m) => ({ default: m.PlatformRuntimeSettingsPage })),
);
const PlatformMfaSetupPage = lazy(() =>
  import('./pages/platform/PlatformMfaSetupPage').then((m) => ({ default: m.PlatformMfaSetupPage })),
);
const KybQueuePage = lazy(() => import('./pages/platform/KybQueuePage').then((m) => ({ default: m.KybQueuePage })));
const PricingExperimentsPage = lazy(() =>
  import('./pages/platform/PricingExperimentsPage').then((m) => ({ default: m.PricingExperimentsPage })),
);
const EnterprisePricingPage = lazy(() =>
  import('./pages/platform/EnterprisePricingPage').then((m) => ({ default: m.EnterprisePricingPage })),
);
const CampaignsAdminPage = lazy(() =>
  import('./pages/platform/CampaignsAdminPage').then((m) => ({ default: m.CampaignsAdminPage })),
);
const InfraMetricsPage = lazy(() =>
  import('./pages/platform/InfraMetricsPage').then((m) => ({ default: m.InfraMetricsPage })),
);

function RouteFallback() {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <CardSkeleton />
      <CardSkeleton />
      <CardSkeleton />
      <CardSkeleton />
    </div>
  );
}

/**
 * LazyPage — Sprint 28c Premium Polish.
 * Route key'i değişince animate-page-enter ile fade-in + slide-up.
 * BRANDBOOK §8.4 — prefers-reduced-motion ile otomatik kapanır.
 */
function LazyPage({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  return (
    <Suspense fallback={<RouteFallback />}>
      <div key={location.pathname} className="animate-page-enter">
        {children}
      </div>
    </Suspense>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/giris" element={<LoginPage />} />
      <Route path="/sifremi-unuttum" element={<ForgotPasswordPage />} />
      <Route path="/kayit" element={<SignupPage />} />
      <Route path="/eposta-dogrula" element={<VerifyEmailPage />} />
      <Route path="/mfa" element={<MfaChallengePage />} />
      <Route path="/dogrulama" element={<EmailOtpPage />} />
      <Route
        path="/telefon-dogrula"
        element={
          <RequireAuth>
            <PhoneVerifyPage />
          </RequireAuth>
        }
      />
      <Route path="/davet/:token" element={<AcceptInvitePage />} />

      <Route
        path="/pricing"
        element={
          <LazyPage>
            <PricingPage />
          </LazyPage>
        }
      />
      <Route
        path="/onboarding"
        element={
          <LazyPage>
            <OnboardingWizard />
          </LazyPage>
        }
      />

      <Route
        element={
          <RequireAuth>
            <DashboardLayout />
          </RequireAuth>
        }
      >
        <Route
          path="/"
          element={
            <LazyPage>
              <OverviewPage />
            </LazyPage>
          }
        />
        <Route
          path="/kartlar"
          element={
            <RequireActiveCompany>
              <LazyPage>
                <CardsPage />
              </LazyPage>
            </RequireActiveCompany>
          }
        />
        {/* Cüzdan (founder 2026-07-20) — havuz + çalışan fonlaması; finans/yönetici rolleri
            (backend read/update Wallet ile aynı küme). */}
        <Route
          path="/cuzdan"
          element={
            <RequireRole roles={['OWNER', 'CEO', 'CFO', 'ACCOUNTANT', 'DEPARTMENT_MANAGER']}>
              <RequireActiveCompany>
                <LazyPage>
                  <WalletPage />
                </LazyPage>
              </RequireActiveCompany>
            </RequireRole>
          }
        />
        <Route
          path="/islemler"
          element={
            <RequireActiveCompany>
              <LazyPage>
                <TransactionsPage />
              </LazyPage>
            </RequireActiveCompany>
          }
        />
        <Route
          path="/fisler"
          element={
            <RequireActiveCompany>
              <LazyPage>
                <ReceiptsPage />
              </LazyPage>
            </RequireActiveCompany>
          }
        />
        <Route
          path="/e-faturalar"
          element={
            <RequireRole roles={['OWNER', 'CEO', 'CFO', 'ACCOUNTANT', 'DEPARTMENT_MANAGER', 'TEAM_LEAD']}>
              <RequireActiveCompany>
                <LazyPage>
                  <EInvoicesPage />
                </LazyPage>
              </RequireActiveCompany>
            </RequireRole>
          }
        />
        <Route
          path="/muhasebe"
          element={
            <RequireRole roles={['OWNER', 'CEO', 'CFO', 'ACCOUNTANT', 'DEPARTMENT_MANAGER']}>
              <LazyPage>
                <AccountingPage />
              </LazyPage>
            </RequireRole>
          }
        />
        <Route
          path="/muhasebe/parasut-callback"
          element={
            <RequireRole roles={['OWNER', 'CEO', 'CFO', 'ACCOUNTANT', 'DEPARTMENT_MANAGER']}>
              <LazyPage>
                <AccountingPage />
              </LazyPage>
            </RequireRole>
          }
        />
        <Route
          path="/raporlar"
          element={
            <RequireAbility action="read" subject="Report">
              <LazyPage>
                <ReportsPage />
              </LazyPage>
            </RequireAbility>
          }
        />
        <Route
          path="/butceler"
          element={
            <RequireAbility action="read" subject="Budget">
              <RequireActiveCompany>
                <LazyPage>
                  <BudgetsPage />
                </LazyPage>
              </RequireActiveCompany>
            </RequireAbility>
          }
        />
        <Route
          path="/profil"
          element={
            <LazyPage>
              <ProfilePage />
            </LazyPage>
          }
        />
        <Route
          path="/ayarlar/bildirimler"
          element={
            <LazyPage>
              <NotificationPreferencesPage />
            </LazyPage>
          }
        />
        <Route
          path="/organizasyon"
          element={
            <RequireRole exclude={SELF_ONLY_ROLES}>
              <LazyPage>
                <OrgChartPage />
              </LazyPage>
            </RequireRole>
          }
        />
        <Route
          path="/admin/yetki-matrisi"
          element={
            <AdminOnly>
              <LazyPage>
                <RoleMatrixPage />
              </LazyPage>
            </AdminOnly>
          }
        />
        <Route
          path="/faturalama"
          element={
            <RequireAbility action="read" subject="Billing">
              <LazyPage>
                <BillingPage />
              </LazyPage>
            </RequireAbility>
          }
        />
        <Route
          path="/iadeler"
          element={
            <LazyPage>
              <ReimbursementsPage />
            </LazyPage>
          }
        />
        <Route
          path="/onaylar"
          element={
            <RequireActiveCompany>
              <LazyPage>
                <ApprovalsPage />
              </LazyPage>
            </RequireActiveCompany>
          }
        />
        <Route
          path="/onay-politikalari"
          element={
            <RequireAbility action="update" subject="ApprovalPolicy">
              <LazyPage>
                <ApprovalPoliciesPage />
              </LazyPage>
            </RequireAbility>
          }
        />
        <Route
          path="/ayarlar/kurumsal-kimlik"
          element={
            <AdminOnly>
              <LazyPage>
                <AdminEnterpriseIdentityPage />
              </LazyPage>
            </AdminOnly>
          }
        />
        <Route
          path="/ayarlar/acil-durum"
          element={
            <AdminOnly>
              <LazyPage>
                <EmergencyPage />
              </LazyPage>
            </AdminOnly>
          }
        />
        <Route
          path="/alarmlar"
          element={
            <RequireAbility action="read" subject="Alarm">
              <LazyPage>
                <AlarmsPage />
              </LazyPage>
            </RequireAbility>
          }
        />
        <Route
          path="/calisanlar"
          element={
            <RequireRole exclude={SELF_ONLY_ROLES}>
              <RequireActiveCompany>
                <LazyPage>
                  <EmployeesPage />
                </LazyPage>
              </RequireActiveCompany>
            </RequireRole>
          }
        />
        <Route
          path="/calisanlar/:id"
          element={
            <RequireRole exclude={SELF_ONLY_ROLES}>
              <RequireActiveCompany>
                <LazyPage>
                  <EmployeeDetailPage />
                </LazyPage>
              </RequireActiveCompany>
            </RequireRole>
          }
        />
        <Route
          path="/departmanlar"
          element={
            <RequireAbility action="read" subject="Department">
              <RequireActiveCompany>
                <LazyPage>
                  <DepartmentsPage />
                </LazyPage>
              </RequireActiveCompany>
            </RequireAbility>
          }
        />
        <Route
          path="/sirket"
          element={
            <LazyPage>
              <CompanyPage />
            </LazyPage>
          }
        />
        <Route
          path="/firma-kaydi"
          element={
            <RequireRole roles={['OWNER', 'CEO', 'CFO']}>
              <LazyPage>
                <CompanyRegistrationPage />
              </LazyPage>
            </RequireRole>
          }
        />
      </Route>

      {/* Platform (Custel operatör) — MFA kurulum: guard'lı, layout'suz standalone. */}
      <Route
        path="/platform/mfa-setup"
        element={
          <RequirePlatform>
            <LazyPage>
              <PlatformMfaSetupPage />
            </LazyPage>
          </RequirePlatform>
        }
      />

      {/* Platform konsolu — RequirePlatform guard + PlatformLayout. */}
      <Route
        element={
          <RequirePlatform>
            <PlatformLayout />
          </RequirePlatform>
        }
      >
        <Route
          path="/platform"
          element={
            <LazyPage>
              <PlatformDashboardPage />
            </LazyPage>
          }
        />
        <Route
          path="/platform/musteriler"
          element={
            <LazyPage>
              <CustomersListPage />
            </LazyPage>
          }
        />
        <Route
          path="/platform/musteriler/yeni"
          element={
            <LazyPage>
              <CompanyCreatePage />
            </LazyPage>
          }
        />
        <Route
          path="/platform/musteriler/:id"
          element={
            <LazyPage>
              <CustomerDetailPage />
            </LazyPage>
          }
        />
        <Route
          path="/platform/metrikler"
          element={
            <LazyPage>
              <SystemMetricsPage />
            </LazyPage>
          }
        />
        <Route
          path="/platform/talepler"
          element={
            <LazyPage>
              <SupportTicketsPage />
            </LazyPage>
          }
        />
        <Route
          path="/platform/erisim-kayitlari"
          element={
            <LazyPage>
              <PlatformAccessLogsPage />
            </LazyPage>
          }
        />
        <Route
          path="/platform/ekip"
          element={
            <LazyPage>
              <PlatformStaffPage />
            </LazyPage>
          }
        />
        <Route
          path="/platform/ayarlar"
          element={
            <LazyPage>
              <PlatformRuntimeSettingsPage />
            </LazyPage>
          }
        />
        <Route
          path="/platform/kyb"
          element={
            <LazyPage>
              <KybQueuePage />
            </LazyPage>
          }
        />
        <Route
          path="/platform/pricing-experiments"
          element={
            <LazyPage>
              <PricingExperimentsPage />
            </LazyPage>
          }
        />
        <Route
          path="/platform/enterprise-pricing"
          element={
            <LazyPage>
              <EnterprisePricingPage />
            </LazyPage>
          }
        />
        <Route
          path="/platform/altyapi"
          element={
            <LazyPage>
              <InfraMetricsPage />
            </LazyPage>
          }
        />
        <Route
          path="/platform/kampanyalar"
          element={
            <LazyPage>
              <CampaignsAdminPage />
            </LazyPage>
          }
        />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
